import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { queryKeys } from "../queryKeys";
import { fetchThumbnailKeys } from "./notifications";
import { useSignedUrls } from "./useSignedUrls";

/**
 * メディアIDだけを持っている面(交換日記のカード・選択中の一覧など)で、
 * サムネイルを出すための署名付きURLを引く。
 *
 * タイムラインは行ごと持っているのでサムネイルのキーも手元にあるが、
 * 中間テーブル経由でIDしか無い面は「ID→キー」を1回挟む必要がある。
 * その2段構えを毎回書くと取り直しの条件がずれるため、ここにまとめる。
 *
 * `mediaIds` は問い合わせのキーに使う。呼ぶ側で useMemo などにより
 * 同じ内容なら同じ配列を渡すこと(毎描画で作り直すと取り直しが止まらない)。
 */
export function useMediaThumbnails(mediaIds: string[]): {
  urlOf: (mediaId: string) => string | undefined;
  /** キーごとの署名付きURL。MediaDetail の thumbnailUrls にそのまま渡せる */
  urls: Record<string, string>;
} {
  const rows = useQuery({
    queryKey: queryKeys.mediaThumbnails(mediaIds),
    queryFn: () => fetchThumbnailKeys(mediaIds),
    enabled: mediaIds.length > 0,
  });

  const keyOfMedia = useMemo(
    () => new Map((rows.data ?? []).map((row) => [row.id, row.thumbnail_key])),
    [rows.data],
  );
  const { urls } = useSignedUrls(useMemo(() => [...keyOfMedia.values()], [keyOfMedia]));

  return {
    urlOf: (mediaId: string) => {
      const key = keyOfMedia.get(mediaId);
      return key === undefined ? undefined : urls[key];
    },
    urls,
  };
}
